// src/components/Login/RegisterForm.tsx
import React, { useState } from 'react'
import { registerUser } from '../../api/userService'
import { useNavigate } from 'react-router-dom'
import { Mail, Lock } from 'lucide-react'
import logo from '../../assets/damelio_logo_blanco.png'
import logo2 from '../../assets/damelio_texto_blanco.png'
import { useTranslation } from 'react-i18next'

interface RegisterFormProps {
  toggleForm: () => void
  onRegisterSuccess: () => void
}

const RegisterForm: React.FC<RegisterFormProps> = ({
  toggleForm,
  onRegisterSuccess,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [email, setEmail]           = useState('')
  const [firstName, setFirstName]   = useState('')
  const [lastName, setLastName]     = useState('')
  const [password, setPassword]     = useState('')
  const [repeatPassword, setRepeatPassword] = useState('')
  const [acceptedPolicy, setAcceptedPolicy] = useState(false)
  const [error, setError]           = useState('')

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (password !== repeatPassword) {
      setError(t('register.errors.passwordsNoMatch'))
      return
    }
    if (!acceptedPolicy) {
      setError(t('register.errors.policyNotAccepted'))
      return
    }

    try {
      const resp = await registerUser(email, password, firstName, lastName)
      if (resp.error_key || resp.error) {
        throw new Error(resp.error_key || resp.error)
      }
      onRegisterSuccess()
    } catch (err: any) {
      let msg = t('register.errors.default')
      switch (err.message) {
        case 'register.errors.duplicateEmail':
          msg = t('register.errors.duplicateEmail')
          break
        case 'register.errors.shortPassword':
          msg = t('register.errors.shortPassword')
          break
        case 'register.errors.dbGeneric':
          msg = t('register.errors.dbGeneric')
          break
      }
      setError(msg)
    }
  }

  return (
    <div className="w-full max-w-md mx-auto space-y-6 text-white px-4 sm:px-0">
      {/* Logos */}
      <div
        className="text-center cursor-pointer transform hover:scale-105 transition duration-300"
        onClick={() => navigate('/')}
      >
        <img src={logo} alt={t('login.altLogo')} className="mx-auto h-24 sm:h-28" />
        <img
          src={logo2}
          alt={t('login.altLogo2')}
          className="mx-auto h-12 sm:h-16 mt-1 sm:mt-2"
        />
      </div>

      <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-center">
        {t('register.title')}
      </h1>

      <form onSubmit={handleRegister} className="space-y-5">
        {/* Email */}
        <div>
          <label
            htmlFor="email"
            className="block text-sm sm:text-base font-medium text-gray-200"
          >
            {t('register.labels.email')}
          </label>
          <div className="relative mt-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Mail className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
            </div>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('register.placeholders.email')}
              required
              className="
                block w-full pl-10 pr-3 py-2 sm:py-2.5
                bg-gray-800 border border-gray-700
                rounded-lg text-white placeholder-gray-500
                focus:outline-none focus:ring-2 focus:ring-blue-500
                focus:border-blue-500 text-sm sm:text-base
                transition
              "
            />
          </div>
          <p className="text-xs sm:text-sm text-blue-400 mt-1">
            {t('register.enterpriseNotice')}
          </p>
        </div>

        {/* Name fields */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="firstName"
              className="block text-sm sm:text-base font-medium text-gray-200"
            >
              {t('register.labels.firstName')}
            </label>
            <input
              type="text"
              id="firstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder={t('register.placeholders.firstName')}
              required
              className="
                mt-1 block w-full px-3 py-2 sm:py-2.5
                bg-gray-800 border border-gray-700
                rounded-lg text-white placeholder-gray-500
                focus:outline-none focus:ring-2 focus:ring-blue-500
                focus:border-blue-500 text-sm sm:text-base
                transition
              "
            />
          </div>
          <div>
            <label
              htmlFor="lastName"
              className="block text-sm sm:text-base font-medium text-gray-200"
            >
              {t('register.labels.lastName')}
            </label>
            <input
              type="text"
              id="lastName"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder={t('register.placeholders.lastName')}
              required
              className="
                mt-1 block w-full px-3 py-2 sm:py-2.5
                bg-gray-800 border border-gray-700
                rounded-lg text-white placeholder-gray-500
                focus:outline-none focus:ring-2 focus:ring-blue-500
                focus:border-blue-500 text-sm sm:text-base
                transition
              "
            />
          </div>
        </div>

        {/* Password fields */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="password"
              className="block text-sm sm:text-base font-medium text-gray-200"
            >
              {t('register.labels.password')}
            </label>
            <div className="relative mt-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
              </div>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('register.placeholders.password')}
                required
                className="
                  block w-full pl-10 pr-3 py-2 sm:py-2.5
                  bg-gray-800 border border-gray-700
                  rounded-lg text-white placeholder-gray-500
                  focus:outline-none focus:ring-2 focus:ring-blue-500
                  focus:border-blue-500 text-sm sm:text-base
                  transition
                "
              />
            </div>
            <p className="text-xs sm:text-sm text-gray-400 mt-1">
              {t('register.passwordHint')}
            </p>
          </div>
          <div>
            <label
              htmlFor="repeatPassword"
              className="block text-sm sm:text-base font-medium text-gray-200"
            >
              {t('register.labels.repeatPassword')}
            </label>
            <div className="relative mt-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
              </div>
              <input
                type="password"
                id="repeatPassword"
                value={repeatPassword}
                onChange={(e) => setRepeatPassword(e.target.value)}
                placeholder={t('register.placeholders.repeatPassword')}
                required
                className="
                  block w-full pl-10 pr-3 py-2 sm:py-2.5
                  bg-gray-800 border border-gray-700
                  rounded-lg text-white placeholder-gray-500
                  focus:outline-none focus:ring-2 focus:ring-blue-500
                  focus:border-blue-500 text-sm sm:text-base
                  transition
                "
              />
            </div>
          </div>
        </div>

        {/* Policy checkbox */}
        <div className="flex items-center">
          <input
            type="checkbox"
            id="privacyPolicy"
            checked={acceptedPolicy}
            onChange={(e) => setAcceptedPolicy(e.target.checked)}
            className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500 border-gray-700 rounded focus:ring-blue-500"
          />
          <label
            htmlFor="privacyPolicy"
            className="ml-2 text-sm sm:text-base text-gray-200"
          >
            {t('register.labels.acceptPolicy')}{' '}
            <a
              href="/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-400 hover:underline"
            >
              {t('register.labels.privacyPolicy')}
            </a>
          </label>
        </div>

        {/* Submit */}
        <button
          type="submit"
          onClick={handleRegister}
          className="
            w-full flex justify-center py-2 px-4
            bg-gradient-to-r from-blue-500 to-indigo-600
            hover:from-blue-600 hover:to-indigo-700
            text-white font-semibold rounded-lg
            shadow-lg shadow-indigo-900/50 transition-transform
            transform hover:scale-105
          "
        >
          {t('register.buttons.register')}
        </button>
      </form>

      {error && <p className="text-red-500 text-center mt-4">{error}</p>}

      <p className="text-center text-sm sm:text-base text-gray-400 mt-6">
        {t('register.alreadyHaveAccount')}
        <span
          onClick={toggleForm}
          className="ml-1 text-blue-400 hover:underline cursor-pointer"
        >
          {t('register.login')}
        </span>
      </p>
    </div>
  )
}

export default RegisterForm
